import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { Button, Field, PixlideLogo } from '@pixlide/ui';
import { ApiError } from '@/api/client';
import { useLogin, useVerifyTwoFactorLogin } from '@/api/queries/auth';
import { loginSchema, type LoginInput } from '@/features/auth/schemas';

/**
 * Messages d'erreur métier, indexés par le code stable renvoyé par l'API — même convention
 * que `frontend/src/features/auth/LoginPage.tsx` : on ne teste jamais le message serveur,
 * qui peut être reformulé sans préavis, uniquement le code.
 */
const ERROR_MESSAGES: Record<string, string> = {
  'auth.invalid_credentials': 'Adresse email ou mot de passe incorrect.',
  'auth.account_locked':
    'Ce compte est temporairement bloqué après plusieurs tentatives. Réessayez dans quelques minutes.',
  'auth.account_disabled': 'Ce compte a été désactivé.',
};

/**
 * Connexion au back-office.
 *
 * Pas d'inscription publique ici : le rôle Platform Admin n'est jamais attribuable via
 * l'API, il n'existe donc aucun parcours de création de compte à câbler dans cette app —
 * seule la connexion à un compte déjà promu Platform Admin (par seed ou SQL direct) a un
 * sens.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const login = useLogin();

  // Non `null` uniquement le temps du second facteur : le compte est déjà authentifié par
  // mot de passe, il ne manque plus que le code — même idiome que `frontend/`.
  const [challengeToken, setChallengeToken] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    const result = await login.mutateAsync(values);

    if (result.twoFactorRequired && result.twoFactorChallengeToken) {
      setChallengeToken(result.twoFactorChallengeToken);

      return;
    }

    await navigate({ to: '/' });
  });

  if (challengeToken !== null) {
    return <TwoFactorChallengeStep challengeToken={challengeToken} onCancel={() => setChallengeToken(null)} />;
  }

  const apiError = login.error instanceof ApiError ? login.error : null;
  const alertMessage = apiError === null ? null : (ERROR_MESSAGES[apiError.code] ?? apiError.message);

  return (
    <div className="auth-canvas flex min-h-dvh items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-card bg-white p-8 shadow-raised">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <PixlideLogo />
          <span className="inline-flex items-center gap-1.5 rounded-pill bg-ink-900 px-3 py-1 text-[11px] font-semibold tracking-wide text-white uppercase">
            <ShieldCheck aria-hidden className="size-3.5" />
            Back-office
          </span>
        </div>

        {alertMessage ? (
          <p role="alert" className="mb-4 rounded-xl bg-danger-100 px-3.5 py-2.5 text-sm text-danger-500">
            {alertMessage}
          </p>
        ) : null}

        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-3">
          <Field
            label="Adresse email"
            type="email"
            autoComplete="email"
            autoFocus
            error={errors.email?.message}
            {...register('email')}
          />

          <Field
            label="Mot de passe"
            type="password"
            revealable
            autoComplete="current-password"
            error={errors.password?.message}
            {...register('password')}
          />

          <Button
            type="submit"
            isLoading={isSubmitting || login.isPending}
            className="mt-2 h-11 w-full rounded-xl"
          >
            Se connecter
            <ArrowRight aria-hidden className="size-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}

const TWO_FACTOR_ERROR_MESSAGES: Record<string, string> = {
  'auth.two_factor_code_invalid': "Ce code n'est pas valide.",
  'auth.two_factor_challenge_invalid': 'Cette vérification a expiré. Reconnectez-vous.',
};

/**
 * Second temps de la connexion sur un compte 2FA : code de l'application d'authentification,
 * ou l'un des codes de récupération saisi tel quel (l'écran ne fait pas la différence, le
 * serveur tente les deux).
 */
function TwoFactorChallengeStep({
  challengeToken,
  onCancel,
}: {
  challengeToken: string;
  onCancel: () => void;
}) {
  const navigate = useNavigate();
  const verify = useVerifyTwoFactorLogin();
  const [code, setCode] = useState('');

  async function submit() {
    if (code.length === 0) {
      return;
    }

    await verify.mutateAsync({ challengeToken, code });
    await navigate({ to: '/' });
  }

  const apiError = verify.error instanceof ApiError ? verify.error : null;
  const alertMessage = apiError === null ? null : (TWO_FACTOR_ERROR_MESSAGES[apiError.code] ?? apiError.message);

  return (
    <div className="auth-canvas flex min-h-dvh items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-card bg-white p-8 shadow-raised">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <PixlideLogo />
          <h1 className="text-sm font-semibold text-ink-900">Vérification en deux étapes</h1>
          <p className="text-xs text-ink-500">
            Saisissez le code affiché par votre application d'authentification.
          </p>
        </div>

        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
          className="flex flex-col gap-6"
        >
          <Field
            label="Code de vérification"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            placeholder="123456"
            error={alertMessage ?? undefined}
            value={code}
            onChange={(event) => {
              setCode(event.target.value.replace(/\D/g, ''));
              verify.reset();
            }}
          />

          <p className="text-[11px] text-ink-500">
            Appareil perdu ? Saisissez plutôt l'un de vos codes de récupération à la place du code.
          </p>

          <div className="flex gap-3">
            <Button type="button" variant="secondary" onClick={onCancel} className="h-11 flex-1 rounded-xl">
              Annuler
            </Button>

            <Button type="submit" isLoading={verify.isPending} className="h-11 flex-1 rounded-xl">
              Vérifier
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
